/**
 * Field primitives for the Reppr Elite drawer forms.
 *
 * Group  — a bordered section: uppercase label, optional right-aligned aide,
 *          two-column grid of fields (or one with cols=1).
 * Field  — label row with a Required/Optional flag, a hairline input box that
 *          carries an optional mono prefix/suffix, and a hint or error below.
 *          The box styles whatever input element is passed as its child; focus
 *          is shown on the box via :focus-within.
 * Impact — the "What this means" panel: label/value rows computed from real
 *          records at the call site. Rows that cannot be computed from stored
 *          data are simply not passed.
 */

import { ReactNode } from 'react'
import styles from './fields.module.css'

export function Group({ label, aide, cols = 2, children }: {
  label: string
  aide?: string
  cols?: 1 | 2
  children: ReactNode
}) {
  return (
    <div className={styles.group}>
      <div className={styles.groupHead}>
        <span className={styles.groupLabel}>{label}</span>
        {aide && <span className={styles.groupAide}>{aide}</span>}
      </div>
      <div className={cols === 1 ? styles.gridOne : styles.gridTwo}>{children}</div>
    </div>
  )
}

export function Field({ label, htmlFor, req, optional, prefix, suffix, hint, error, full, bare, children }: {
  label: string
  htmlFor?: string
  req?: boolean
  optional?: boolean
  /** Mono glyph before the input, e.g. "$". */
  prefix?: string
  /** Mono unit after the input, e.g. "%", "YEARS". */
  suffix?: string
  hint?: string
  error?: string
  /** Span both grid columns. */
  full?: boolean
  /** Skip the input box — for composite widgets that carry their own chrome. */
  bare?: boolean
  children: ReactNode
}) {
  return (
    <div className={`${styles.field} ${full ? styles.full : ''}`}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={htmlFor}>{label}</label>
        {req && <span className={styles.req}>Required</span>}
        {!req && optional && <span className={styles.optional}>Optional</span>}
      </div>
      {bare ? children : (
        <div className={`${styles.box} ${error ? styles.boxError : ''}`}>
          {prefix && <span className={styles.prefix}>{prefix}</span>}
          {children}
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </div>
      )}
      {error
        ? <div className={styles.error}>{error}</div>
        : hint && <div className={styles.hint}>{hint}</div>}
    </div>
  )
}

export type ImpactTone = 'primary' | 'accent' | 'positive' | 'caution' | 'negative'

export interface ImpactRow {
  label: string
  value: string
  tone?: ImpactTone
}

export function Impact({ rows, note, aide = 'Updates as you type' }: {
  rows: ImpactRow[]
  note?: string
  aide?: string
}) {
  if (rows.length === 0) return null
  return (
    <div className={styles.impact}>
      <div className={styles.impactHead}>
        <span className={styles.impactLabel}>What this means</span>
        <span className={styles.impactAide}>{aide}</span>
      </div>
      {rows.map(r => (
        <div className={styles.impactRow} key={r.label}>
          <span className={styles.impactRowLabel}>{r.label}</span>
          <span className={`${styles.impactValue} ${styles[`tone_${r.tone ?? 'primary'}`]}`}>
            {r.value}
          </span>
        </div>
      ))}
      {note && <p className={styles.impactNote}>{note}</p>}
    </div>
  )
}
